import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "./client";
import { accountsQueryKey } from "./accounts";

/**
 * Something the household is putting money aside for (`model.md` §3.8).
 *
 * `is_achieved` is a decision, not a threshold. It is emphatically not
 * "progress ≥ 100%": a household that found the bike second-hand is finished at
 * 60%, and one still saving past a round number is not finished at 105%. The
 * screen may say both things — the money is there, and nobody has called it done
 * — because they are different sentences.
 */
export interface Goal {
  id: string;
  name: string;
  /** A positive magnitude in the goal's currency. */
  target_amount_minor: number;
  currency_code: string;
  target_date?: string;
  linked_account_id?: string;
  is_achieved: boolean;
  achieved_at?: string;
}

/** A goal beside what has gone into it (`model.md` §4.3), computed on read. */
export interface GoalProgress {
  goal: Goal;
  /** §4.3's numerator. It can exceed the target and it can be negative. */
  saved_minor: number;
  remaining_minor: number;
  /** The truth, unclamped — including the part above 1. */
  ratio: number;
  /** §4.3's "clamped for display only": the one a bar's width uses. */
  display_ratio: number;
  /** Arithmetic. `goal.is_achieved` is the decision. */
  target_reached: boolean;
  contributions: number;
  /** What the rest would cost per month to arrive on time. Absent when there is nothing to say. */
  monthly_pace_minor?: number;
}

/** One payment towards a goal. Positive puts money in; negative takes it out. */
export interface Contribution {
  id: string;
  savings_goal_id: string;
  transaction_id?: string;
  amount_minor: number;
  currency_code: string;
  contributed_on: string;
  note?: string;
}

interface List<T> {
  items: T[];
}

export const goalsQueryKey = (householdId: string) =>
  ["households", householdId, "goals"] as const;

/**
 * The household's goals with their progress.
 *
 * Achieved ones are asked for too, and the server sorts them last. A screen that
 * hid them would take the decision away the moment it was made: "Doch nicht
 * erreicht" would be a button nobody could ever reach. It is the same reasoning
 * that keeps a skipped occurrence in K-24's list — changing your mind should be
 * as cheap as deciding.
 */
export function useGoals(householdId: string | undefined) {
  return useQuery({
    queryKey: [...goalsQueryKey(householdId ?? ""), "progress"],
    queryFn: ({ signal }) =>
      apiFetch<List<GoalProgress>>(
        `/api/v1/households/${householdId}/goals?include_achieved=true`,
        { signal },
      ),
    enabled: Boolean(householdId),
  });
}

export interface CreateGoalInput {
  name: string;
  target_amount_minor: number;
  target_date?: string;
}

export function useCreateGoal(householdId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateGoalInput) =>
      apiFetch<Goal>(`/api/v1/households/${householdId}/goals`, {
        method: "POST",
        body: input,
      }),
    onSuccess: () => client.invalidateQueries({ queryKey: goalsQueryKey(householdId) }),
  });
}

/**
 * Put money towards a goal, or take some back out.
 *
 * The amount is signed from the pot's point of view, which is deliberately not
 * the transaction's — see `model.md` §3.8.
 */
export function useContribute(householdId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ goalId, amountMinor }: { goalId: string; amountMinor: number }) =>
      apiFetch<Contribution>(
        `/api/v1/households/${householdId}/goals/${goalId}/contributions`,
        { method: "POST", body: { amount_minor: amountMinor } },
      ),
    // Only the goals: a manual contribution moves no balance, because no money
    // moved — it says where money that was already there is meant to go.
    onSuccess: () => client.invalidateQueries({ queryKey: goalsQueryKey(householdId) }),
  });
}

/** Mark a goal finished, or take the decision back. */
export function useSetAchieved(householdId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ goalId, achieved }: { goalId: string; achieved: boolean }) =>
      apiFetch<Goal>(`/api/v1/households/${householdId}/goals/${goalId}/achieved`, {
        method: "PUT",
        body: { is_achieved: achieved },
      }),
    onSuccess: () => client.invalidateQueries({ queryKey: goalsQueryKey(householdId) }),
  });
}

export function useDeleteGoal(householdId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (goalId: string) =>
      apiFetch<void>(`/api/v1/households/${householdId}/goals/${goalId}`, {
        method: "DELETE",
      }),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: goalsQueryKey(householdId) }),
        client.invalidateQueries({ queryKey: accountsQueryKey(householdId) }),
      ]),
  });
}
